import { Controller } from '@hotwired/stimulus';

type DateTimeMode = 'now' | 'selected-date';

export class LocalDateTimeController extends Controller<HTMLInputElement> {
    static values = {
        mode: { type: String, default: 'now' },
        date: String,
        preserveValue: { type: Boolean, default: false },
    };

    declare readonly modeValue: DateTimeMode;
    declare readonly dateValue: string;
    declare readonly hasDateValue: boolean;
    declare readonly preserveValueValue: boolean;
    private hasUserEdited = false;
    private readonly markUserEdited = () => {
        this.hasUserEdited = true;
    };
    private readonly refreshIfSafe = () => {
        this.setDefaultValue();
    };

    connect(): void {
        this.element.addEventListener('input', this.markUserEdited);
        this.element.addEventListener('change', this.markUserEdited);
        window.addEventListener('pageshow', this.refreshIfSafe);
        document.addEventListener('turbo:load', this.refreshIfSafe);
        document.addEventListener('turbo:render', this.refreshIfSafe);
        this.setDefaultValue();
    }

    disconnect(): void {
        this.element.removeEventListener('input', this.markUserEdited);
        this.element.removeEventListener('change', this.markUserEdited);
        window.removeEventListener('pageshow', this.refreshIfSafe);
        document.removeEventListener('turbo:load', this.refreshIfSafe);
        document.removeEventListener('turbo:render', this.refreshIfSafe);
    }

    private setDefaultValue(): void {
        if (this.hasUserEdited) {
            return;
        }

        if (this.preserveValueValue && this.element.value.trim() !== '') {
            return;
        }

        this.element.value = this.formatDateTimeLocal(this.resolveDefaultDate());
    }

    private resolveDefaultDate(): Date {
        if (this.modeValue !== 'selected-date' || !this.hasDateValue) {
            return new Date();
        }

        const [year, month, day] = this.dateValue.split('-').map((part) => Number.parseInt(part, 10));
        if (!year || !month || !day) {
            return new Date();
        }

        const now = new Date();
        return new Date(year, month - 1, day, now.getHours(), now.getMinutes(), 0, 0);
    }

    private formatDateTimeLocal(date: Date): string {
        const pad = (value: number): string => value.toString().padStart(2, '0');
        return [
            date.getFullYear(),
            pad(date.getMonth() + 1),
            pad(date.getDate()),
        ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
}
